import { Clipboard } from "@angular/cdk/clipboard";
import { ComponentType } from "@angular/cdk/portal";
import { Component, OnDestroy, OnInit, TemplateRef } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { MatLegacySlideToggleChange as MatSlideToggleChange } from "@angular/material/legacy-slide-toggle";
import { ActivatedRoute, Params, Router, RouterEvent } from "@angular/router";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import { canViewDashboard, createUserPermissionFilter } from "@app/_helpers/permission";
import { toPromise } from "@app/_helpers/promise";
import { useUserRulebreaksParser } from "@app/_helpers/rulebreaks/utils";
import { createEncryptedRxValue } from "@app/_helpers/rx-value";
import { createRxValue, distinctUntilChangedJson, fromRxValue, hasChange } from "@app/_helpers/utils";
import { UtilService } from "@app/_services/util.service";
import { AppService } from "@app/app.service";
import {
	AuditFilterData,
	AuditFilterDefaultData,
} from "@app/components/audit-filter-popover/audit-filter-popover.component";
import { ContingentExportDialogComponent } from "@app/components/contingent-export-dialog/contingent-export-dialog.component";
import { ReportsExportDialogComponent } from "@app/components/reports-export-dialog/reports-export-dialog.component";
import { ScheduleExportDialogComponent } from "@app/components/schedule-export-dialog/schedule-export-dialog.component";
import RoundingConfig from "@app/shared/dialogs/rounding-dialog/models/rounding-config";
import { RoundingDialogComponent } from "@app/shared/dialogs/rounding-dialog/rounding-dialog.component";
import { ItemBillingFlag } from "@app/shared/filters/item-billing-flag";
import { ItemCompletedFlag } from "@app/shared/filters/item-completed-flag";
import { MatRightSideSheet } from "@app/shared/mat-right-side-sheet/mat-right-side-sheet";
import { DashboardConfig, DashboardTabTypes } from "@app/shared/save-dashboard-dialog/dashboardConfig";
import { SaveDashboardDialogComponent } from "@app/shared/save-dashboard-dialog/save-dashboard-dialog.component";
import { getTimeRangeByKey, TRange } from "@app/shared/time-range-picker/time-range-constants";
import { ShellComponent } from "@app/shell/shell.component";
import { environment } from "@env/environment";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	ApplicationSettingsQuery,
	ComegoQuery,
	ComegoService,
	Logger,
	MyTimesQuery,
	MyTimesService,
	NotifyService,
	SearchService,
	Tag,
	TagsQuery,
	Time,
	TimesService,
	UserSettingsQuery,
} from "@timeghost/web-api";
import { RoundingData } from "@timeghost/web-api/lib/services/times.service";
import { differenceInDays, isSameDay, isWithinInterval } from "date-fns/esm";
import { chain, sum } from "lodash-es";
import { DateTime } from "luxon";
import { combineLatest, Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap } from "rxjs/operators";
import { GroupType } from "./data-table/data-table.component";
import {
	hasReportsShareFilterParams,
	permissionKindFromTab,
	ReportsReloadOptions,
	ReportsService,
} from "./reports.service";
import { ReportsDownloadDialogComponent } from "./reports-download-dialog/reports-download-dialog.component";
import { ReportItem, useTransformTimeToReportItem } from "./utils";

type DataArguments = [
	string[],
	string[],
	string[],
	string[],
	string[],
	Tag[],
	string[],
	ItemCompletedFlag,
	TRange,
	{ [key: string]: boolean },
	AuditFilterData,
];
type SavedDashboard = DashboardConfig & {
	editing: boolean;
	workspaceId: string;
	user: { id: string; name: string };
	paritionKey: string;
	cosmosEntityName: string;
};
@UntilDestroy()
@Component({
	selector: "app-reports",
	templateUrl: "./reports.component.html",
	styleUrls: ["./reports.component.scss"],
})
export class ReportsComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();

	constructor(
		private shell: ShellComponent,
		private dialog: MatDialog,
		private reportsService: ReportsService,
		private myTimesService: MyTimesService,
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery,
		private timesService: TimesService,
		private myTimeQuery: MyTimesQuery,
		private translate: TranslateService,
		private appService: AppService,
		private userSettingsQuery: UserSettingsQuery,
		private router: Router,
		private tagsQuery: TagsQuery,
		private route: ActivatedRoute,
		private appSettings: ApplicationSettingsQuery,
		private searchService: SearchService,
		private notifyService: NotifyService,
		private sheet: MatRightSideSheet,
		private clipboard: Clipboard
	) {}
	readonly currentPage = this.reportsService.currentPage;
	get isScheduleExportEnabled() {
		return this.currentPage.value?.split(".", 2)[0] === "workinghours";
	}
	readonly taskFilter = this.reportsService.taskFilter;
	readonly groupFilter = this.reportsService.groupFilter;
	readonly clientFilter = this.reportsService.clientFilter;
	readonly projectFilter = this.reportsService.projectFilter;
	readonly tagFilter = this.reportsService.tagFilter;
	readonly comegoTypeFilter = this.reportsService.comegoTypeFilter;
	readonly teamFilter = this.reportsService.teamFilter;
	readonly auditFilter = this.reportsService.auditFilter;
	readonly auditFilter$active = this.reportsService.auditFilter$active;
	readonly billableFilter = this.reportsService.billableFilter;
	readonly billableFilter$active = this.reportsService.billableFilter$active;
	readonly range = this.reportsService.range;
	readonly lastDashboard = this.reportsService.lastDashboard;
	readonly showFilters = this.reportsService.showFilters;
	readonly showFilters$ = this.reportsService.showFilters$;
	readonly canViewAmount = this.reportsService.canViewAmount;
	readonly amountToggle = this.reportsService.amountToggle;
	readonly completedFilter = this.reportsService.completedFilter;
	readonly showCompletedProjects$ = this.reportsService.showCompletedProjects$;
	readonly projecthoursEnabled$ = this.reportsService.projecthoursEnabled$;
	readonly comegoEnabled$ = this.appService.comegoEnabled$;
	readonly comegoExportVisible$ = this.reportsService.comegoExportVisible$;
	readonly visibleNav$ = this.reportsService.visibleNav$;
	readonly scheduleEnabled$ = this.reportsService.scheduleEnabled$;
	readonly data$workinghours = this.reportsService.workinghours.asObservable();
	readonly data$projecthours = this.reportsService.projecthours.asObservable();
	readonly userIsAdmin = this.reportsService.userIsAdmin;
	readonly userIsWorkspaceManager = this.reportsService.userIsWorkspaceManager;
	readonly workinghours = this.reportsService.workinghours;
	readonly projecthours = this.reportsService.projecthours;
	readonly employeeFilterIncludesRemoved = this.reportsService.employeeFilterIncludesRemoved$;
	readonly noStartDateToggle = this.reportsService.noStartDateToggle;
	readonly dashboards = this.reportsService.dashboards;
	readonly untilDate = this.reportsService.untilDate;
	readonly viewOnlyOwnContingent = this.reportsService.viewOnlyOwnContingent;
	readonly lastContingentUrl = this.reportsService.lastContingentUrl;
	readonly permissions$ = this.reportsService.permissions$;
	readonly legacyDate$ = this.reportsService.legacyDate$;
	readonly theme = this.reportsService.theme;
	readonly isDefaultFilter$ = this.reportsService.isDefaultFilter$;
	readonly dashboardsPinnedByUser = this.reportsService.dashboardsPinnedByUser$;

	ngOnDestroy() {
		this.reportsService.clearReportData();
		this.data$all.unsubscribe();
		this.data$comego.unsubscribe();
		this.data$tasks.unsubscribe();
		this.data$projects.unsubscribe();
		this.data$tags.unsubscribe();
		this.data$sharedFilter.next(null);
		this.destroy$.next();
		this.destroy$.complete();
	}
	resetFilter(keepLastDashboard?: boolean) {
		this.activeFavDashboard.next(null);
		this.taskFilter.value =
			this.groupFilter.value =
			this.clientFilter.value =
			this.projectFilter.value =
			this.tagFilter.value =
			this.comegoTypeFilter.value =
				[];
		this.teamFilter.value = [this.userSettingsQuery.getValue().id];

		this.auditFilter.value = AuditFilterDefaultData;
		this.billableFilter.value = ItemBillingFlag.BOTH;
		this.completedFilter.value = ItemCompletedFlag.NOT_COMPLETED;
		this.range.update(() => {
			const rType = getTimeRangeByKey("time-range.preset.week");
			const newData = {
				...(rType || {}),
			} as TRange;
			if (rType) rType.afterParse?.(newData);
			return newData;
		});
		if (!keepLastDashboard) {
			this.lastDashboard.update(null);
			this.activeFavDashboard.update(null);
		}
	}
	onProjectShowCompletedChange(showCompleted: boolean) {
		this.completedFilter.value = showCompleted ? ItemCompletedFlag.BOTH : ItemCompletedFlag.NOT_COMPLETED;
	}
	readonly activeSort = this.reportsService.detailedSort;

	readonly timeLoading$ = this.reportsService.timeLoading$;
	get timeLoading() {
		return this.reportsService.timeLoading;
	}
	set timeLoading(val: boolean) {
		this.reportsService.timeLoading = val;
	}

	private _timeData = createRxValue<any>(null);
	readonly timeData = this._timeData.asObservable().pipe(distinctUntilChanged(), untilDestroyed(this));
	private _pieData = createRxValue<any>(null);
	readonly pieData = this._pieData.asObservable().pipe(distinctUntilChanged(), untilDestroyed(this));
	get currencyCode() {
		return "attach_money";
	}
	get isTeams() {
		return this.appService.isTeams();
	}
	private teamDataSource = this.myTimeQuery.selectAll();
	readonly teamDataSource$ = this.teamDataSource.pipe(distinctUntilChanged());
	summed(times: Time[]) {
		return UtilService.parseMS(sum(times.filter((y) => y.timeDiff > 0).map((y) => y.timeDiff)) * 1000, {
			showSeconds: false,
		});
	}
	summedBilled(times: Time[]) {
		return UtilService.parseMS(
			sum(times.filter((y) => !!y.billable && y.timeDiff > 0).map((y) => y.timeDiff)) * 1000,
			{
				showSeconds: false,
			}
		);
	}
	summedNonBilled(times: Time[]) {
		return UtilService.parseMS(
			sum(times.filter((y) => !y.billable && y.timeDiff > 0).map((y) => y.timeDiff)) * 1000,
			{
				showSeconds: false,
			}
		);
	}
	get locale() {
		return this.translate.currentLang;
	}
	toggleNav() {
		this.shell.sidenav.toggle();
	}
	get isMobile() {
		return this.shell.isMobile;
	}
	readonly range$ = this.reportsService.range$;
	get startDate() {
		return this.range$.pipe(map((x) => x.from));
	}
	get endDate() {
		return this.range$.pipe(map((x) => x.to));
	}
	get group() {
		return this.reportsService.groupBy.value;
	}
	set group(val: GroupType) {
		this.reportsService.groupBy.next(val);
	}
	readonly group$ = this.reportsService.groupBy
		.asObservable()
		.pipe(startWith(GroupType.Project), distinctUntilChanged());
	groupChange(ev: GroupType) {
		this.group = ev;
	}
	private team: boolean;
	setTeam(val: MatSlideToggleChange) {
		this._teamData.next(!!val.checked);
		this.team = val.checked;
	}
	private _teamData = createRxValue(false);
	readonly teamData = this._teamData.asObservable().pipe(startWith(), distinctUntilChanged());
	teamStream: Observable<Observable<Time[]>>;
	private _timeStatistics = createRxValue<IReportTimeStatistics>(null);
	readonly timeStatistics$ = this._timeStatistics.asObservable().pipe(distinctUntilChanged());
	get timeStatistics() {
		return this._timeStatistics.value;
	}
	set timeStatistics(val: IReportTimeStatistics) {
		this._timeStatistics.next(val);
	}
	public favDashboards$?: Observable<any>;
	public activeFav$: Observable<string>;

	reload(options?: ReportsReloadOptions) {
		this.reportsService.reload(options);
	}
	readonly users$ = this.reportsService.users$;
	readonly data$ = this.reportsService.data$;

	readonly data$all = fromRxValue(
		combineLatest([this.data$workinghours, this.data$projecthours]).pipe(
			untilDestroyed(this),
			map(([comego, times]) => ({ comego, times }))
		),
		{ comego: [], times: [] }
	);
	readonly data$comego = fromRxValue(this.data$workinghours.pipe(untilDestroyed(this)));
	readonly rulebreakParser = useUserRulebreaksParser();
	readonly data$sharedFilter = createRxValue<RangeFilterData>(null);
	readonly data$filtered = this.reportsService.data$filtered;
	readonly roundingData$ = this.reportsService.roundingData$;
	readonly stats$ = this.reportsService.stats$;
	readonly data$stats = this.reportsService.data$stats;
	roundingHandler<T extends ReportItem>(overrideEnabled?: boolean) {
		return this.reportsService.roundingHandler<T>(overrideEnabled);
	}
	readonly exportDisabled$ = this.timeLoading$;
	readonly exportVisible$ = combineLatest([this.permissions$.value$, this.currentPage.asObservable()]).pipe(
		map(([permissions, page]) => {
			if (!permissions?.exportPermissions) return false;
			if (page === "contingent") return !!permissions.exportPermissions.exportContingent;
			if (page === "workinghours") return !!permissions.exportPermissions.exportWorkinghours;
			return !!permissions.exportPermissions.exportProjectHours;
		})
	);
	readonly data$tasks = fromRxValue(
		combineLatest([this.reportsService.tasks.asObservable(), this.data$sharedFilter.asObservable()]).pipe(
			untilDestroyed(this),
			map(([tasks, dataFilter]) => {
				if (!tasks?.length || !dataFilter?.tasks.length) return [];
				return tasks.filter((task) => dataFilter.tasks.find((x) => x.id == task.id));
			})
		)
	);
	readonly data$projects = fromRxValue(
		this.data$sharedFilter.asObservable().pipe(
			untilDestroyed(this),
			map((x) => x?.projects?.filter((t) => !!t) || [])
		)
	);
	readonly data$tags = fromRxValue(
		this.data$sharedFilter.asObservable().pipe(
			untilDestroyed(this),
			map((x) =>
				[
					...(x?.tags?.filter((t) => !!t) || []),
					...(x?.projects.reduce((l, r) => {
						// @ts-ignore
						if (r?.tags?.length) l.push(...r.tags);
						return l;
					}, []) || []),
				]
					.filter(Boolean)
					.uniqBy((x) => x.id)
			)
		)
	);
	readonly lastVisitDashboard = createEncryptedRxValue<string>("lastVisitDashboard", "/dashboard", {
		addOperators: (s) =>
			s.pipe(
				filter((url) => url?.startsWith("/dashboard")),
				distinctUntilChanged()
			),
	});
	readonly transformTimeToReportItem = useTransformTimeToReportItem();
	private didHydrateFromQuery = false;

	private ensureAllowedInitialRoute(queryParams: Params) {
		if (this.didHydrateFromQuery) return;
		this.didHydrateFromQuery = true;
		const user = this.userSettingsQuery.getValue();
		const permissions = canViewDashboard(user);
		if (!permissions || hasReportsShareFilterParams(queryParams)) return;
		let url = permissions.dashboardUrl;
		const oldUrl = this.lastVisitDashboard.value;
		switch (oldUrl) {
			case "/dashboard/schedules":
				if (permissions.workingHoursPermission) url = oldUrl;
				break;
			case "/dashboard/contingent":
				if (permissions.timecontingentPermission) url = oldUrl;
				break;
			case "/dashboard":
				if (permissions.projectPermission) url = oldUrl;
				break;
			case "/dashboard/detailed":
				if (permissions.projectPermission) url = oldUrl;
				break;
		}
		if (this.router.url !== url) {
			this.router.navigate([url], {
				queryParams,
				replaceUrl: true,
			});
		}
	}

	ngOnInit() {
		this.currentPage
			.asObservable()
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.activeFavDashboard.update(null);
			});
		this.reportsService.getDashboardFromUser();
		this.reportsService.loadTasks();
		this.route.queryParams.pipe(distinctUntilChangedJson(), untilDestroyed(this)).subscribe((queryParams) => {
			this.ensureAllowedInitialRoute(queryParams);
			const { applied, stripKeys } = this.reportsService.consumeDeepLinkQuery(queryParams);
			if (applied) {
				this.lastDashboard.update(null);
				this.activeFavDashboard.update(null);
				const clearParams = stripKeys.reduce((acc, key) => {
					acc[key] = null;
					return acc;
				}, {} as Params);
				this.router.navigate([], {
					relativeTo: this.route,
					queryParams: clearParams,
					queryParamsHandling: "merge",
					replaceUrl: true,
				});
			}
			this.reportsService.updateRangeFromQuery(queryParams);
		});
		this.router.events
			.pipe(
				filter((event) => {
					const isRouteEvent = event instanceof RouterEvent;
					if (!isRouteEvent) return false;
					return !!event.url?.startsWith("/dashboard");
				}),
				debounceTimeAfterFirst(10),
				untilDestroyed(this)
			)
			.subscribe((sourceEvent: any) => {
				const event = sourceEvent as RouterEvent;
				log.debug("url", event.url);
				const recentPath = new URL(event.url, location.origin).pathname;
				this.lastVisitDashboard.next(recentPath);
			});
		this.reportsService.initReportsView();
		this.range$
			.pipe(
				untilDestroyed(this),
				debounceTime(350),
				distinctUntilChangedJson(),
				map(({ from, to }) => ({ from, to })),
				switchMap((range) => this.searchService.getProjectsTasksAndTagsInTimeRange(range.from, range.to))
			)
			.subscribe((fd) => {
				this.data$sharedFilter.value = fd;
			});

		this.notifyService.onMessage
			.asObservable()
			.pipe(
				untilDestroyed(this),
				filter((x) => {
					return (
						x.payload?.cosmosEntityName === "times" &&
						(x.type === 1 ? this.reportsService.testProjectTimeFilter(x.payload as Time) : true)
					);
				})
			)
			.subscribe(({ type, payload }: { type: number; payload: Time }) => {
				const idx = this.reportsService.projecthours.value.findIndex((t) => t.id === payload.id);
				if (type === 1 && idx !== -1) type = 2;
				if (type === 2 && idx !== -1 && !this.reportsService.testProjectTimeFilter(payload)) type = 3; // delete item locally if test failed
				if (type === 1 || type === 2) {
					const range = this.range.value;
					const start = new Date(payload.start);
					if (!isWithinInterval(start, { start: range.from, end: range.to })) {
						type = 3;
					}
				}
				if (type === 1)
					this.reportsService.projecthours.update((state) => {
						state.push(...(this.transformTimeToReportItem(payload) as ReportItem<Time>[]));
						return state;
					}, true);
				else if (type === 2 && idx !== -1) {
					this.reportsService.projecthours.update((state) => {
						state.splice(idx, 1, ...(this.transformTimeToReportItem(payload) as ReportItem<Time>[]));
						return state;
					}, true);
				} else if (type === 3 && idx !== -1)
					this.reportsService.projecthours.update((state) => {
						state = state.filter((t) => t.id !== payload.id);
						return state;
					}, true);
				log.debug("[notify] :: times", type, "id: ", payload.id, "idx: ", idx, payload);
			});
		combineLatest([
			this.reportsService.reportsTab.asObservable(),
			this.userSettingsQuery.select(),
			this.teamFilter.asObservable(),
		])
			.pipe(untilDestroyed(this), distinctUntilChangedJson())
			.subscribe(([tab, currentUser, users]) => {
				if (!tab) return;
				const userFilter = createUserPermissionFilter(currentUser, permissionKindFromTab(tab));
				const filteredUsers = userFilter(users.map((d: string) => ({ id: d })))?.map(
					(d: { id: string }) => d.id
				);
				if (filteredUsers && hasChange(users, filteredUsers)) this.teamFilter.next(filteredUsers);
			});

		this.viewOnlyOwnContingent
			.pipe(untilDestroyed(this))
			.subscribe((value) => (value ? this.noStartDateToggle.update(true) : null));
	}
	goBack(): void {
		if (this.lastContingentUrl.value) {
			this.router.navigateByUrl(this.lastContingentUrl.value);
			this.lastContingentUrl.update(null);
		}
	}
	onRangeUpdate(r: TRange) {
		this.reportsService.onRangeUpdate(r);
	}
	shareFilters() {
		const url = this.reportsService.buildShareUrl();
		const ok = this.clipboard.copy(url);
		if (ok) this.appService.notifySuccess("reports.shareCopied");
		else this.appService.notifyInfo("reports.shareFailed");
	}
	testFilter(time: Time) {
		return this.reportsService.testProjectTimeFilter(time);
	}
	getExportFilters() {
		return this.reportsService.getExportFilters();
	}
	downloadExcel(options?: { groupedByProject?: boolean; timeZone?: string }) {
		const user = this.userSettingsQuery.getValue();
		const ref = this.openDownloadDialog(),
			filters = this.getExportFilters(),
			times = this.reportsService.projecthours.value,
			timeZone = options?.timeZone ? options?.timeZone : ("KEEP" as any),
			rounding = this.reportsService.getRoundingOptions() as RoundingData,
			users = filters.users.filter((x) => times.find((t) => t.userId === x));

		(user.workspace.settings?.workinghoursEnabled && this.isScheduleExportEnabled
			? this.comegoService.downloadExcelReport({
					range: { start: DateTime.fromJSDate(filters.startDate), end: DateTime.fromJSDate(filters.endDate) },
					type: "excel",
					fillNonTrackedDays: true,
					locale: "en-US",
					users,
				})
			: this.timesService.downloadExcelReport({
					...(filters as any),
					range: { from: filters.startDate, to: filters.endDate },
					type: "excel",
					fillNonTrackedDays: true,
					locale: "en-US",
					timeZone,
					rounding,
					users: filters.users.filter((x) => times.find((t) => t.userId === x)),
				})
		).finally(() => {
			setTimeout(() => {
				ref.close();
			}, 1000);
		});
	}
	downloadCsv() {
		const user = this.userSettingsQuery.getValue();
		const ref = this.openDownloadDialog(),
			{ startDate, endDate, ...filters } = this.getExportFilters(),
			rounding = this.reportsService.getRoundingOptions() as RoundingData,
			users = filters.users;
		(user.workspace.settings?.workinghoursEnabled && this.isScheduleExportEnabled
			? this.comegoService.downloadCsvReport({
					range: { start: DateTime.fromJSDate(startDate), end: DateTime.fromJSDate(endDate) },
					type: "csv",
					fillNonTrackedDays: true,
					locale: "en-US",
					users,
				})
			: this.timesService.downloadCsvReport({
					startDate,
					endDate,
					rounding: this.reportsService.getRoundingOptions() as RoundingData,
				})
		).finally(() => {
			setTimeout(() => {
				ref.close();
			}, 1000);
		});
	}
	openFlow() {
		const win = window.open(
			"https://emea.flow.microsoft.com/de-de/connectors/?filter=timeghost&category=all",
			"_blank"
		);
		win.focus();
	}

	openRoundingDialog() {
		this.dialog
			.open(RoundingDialogComponent, {
				data: new RoundingConfig({
					data: this.appService.roundingData,
				}),
			})
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				switchMap(async (x) => {
					if (!!x) {
						await this.appService.updateRounding(x);
					}
					log.debug(x);
					return x;
				})
			)
			.subscribe();
	}
	readonly activeSort$ = this.reportsService.contingentSort;
	openExportDialog() {
		const filter = this.getExportFilters();
		const user = this.userSettingsQuery.getValue();
		const isSchedule = this.isScheduleExportEnabled;
		const page = this.currentPage.value;

		if (
			(page === "workinghours" &&
				!(
					user.workspace.settings.workinghoursEnabled
						? this.reportsService.workinghours.value
						: this.reportsService.projecthours.value
				)?.length) ||
			((page === "project.simple" || page == "project.detailed") &&
				!this.reportsService.projecthours?.value?.length) ||
			(page == "contingent" &&
				!user.workspace.users.find(
					(d) =>
						this.teamFilter.value.includes(d.id) && (this.noStartDateToggle.value || !!d["employment_date"])
				))
		) {
			this.appService.notifyInfo("reports.exportNoValues");
			return;
		}

		if (!isSchedule)
			filter.users =
				filter.users?.filter((u) => {
					return this.data$filtered.value.find((t) => t.userId === u);
				}) || []; // only allow users that have times in range
		if (page === "contingent") {
			const { maxCutOffDate } = this.reportsService.contingentFilterState.value;
			const listedUserIds = this.teamFilter.value.filter((id) => {
				const workspaceUser = user.workspace.users.find((d) => d.id === id);
				if (!workspaceUser || workspaceUser.removed) return false;
				return this.noStartDateToggle.value || !!workspaceUser["employment_date"];
			});
			return this.dialog.open(ContingentExportDialogComponent, {
				data: {
					type: "excel",
					users: listedUserIds,
					userWithOutStart: this.noStartDateToggle.value,
					sortBy: this.reportsService.contingentSort.value,
					cutOffDate: filter.cutOffDate,
					maxCutOffDate,
				},
			});
		}
		return this.dialog.open(
			(isSchedule ? ScheduleExportDialogComponent : ReportsExportDialogComponent) as ComponentType<any>,
			{
				data: {
					filter,
					comego: isSchedule && !!user.workspace.settings?.workinghoursEnabled,
				},
			}
		);
	}
	openDownloadDialog() {
		return this.dialog.open(ReportsDownloadDialogComponent, {
			disableClose: true,
		});
	}

	private _parseGrouped(x: Time[], grp: GroupType) {
		return chain(x)
			.groupBy((y: Time) => {
				// @ts-ignore
				if (grp === GroupType.Client && y.client) {
					// @ts-ignore
					return y.client.id;
				}
				if (grp === GroupType.Task && y.task) {
					return y.task.id;
				}
				if (grp === GroupType.User && y.user) {
					return y.user.id;
				}
				return y.project.id;
			})
			.toPairs()
			.value()
			.map(([key, value]: [string, Time[]]): [string, Time[]] => {
				value = value.filter((y) => !!y.task);
				return [key, value];
			});
	}
	readonly activeFavDashboard = this.reportsService.activeFavDashboard;
	async savedDashboardMenu(
		createNew: boolean,
		updateOrNewTemplate?: TemplateRef<any>,
		confirmUpdateDialog?: TemplateRef<any>
	) {
		let openSheet = true;
		const id = this.activeFavDashboard.value?.id;
		//  Ask for Update or create New
		if (updateOrNewTemplate && id) {
			const update = await toPromise(this.dialog.open(updateOrNewTemplate, {}).afterClosed());
			if (!update) return;
			if (update === 1) {
				//Confirm update
				openSheet = await this.updateDashboardAfterConfirm(updateOrNewTemplate, confirmUpdateDialog);
			}
		}
		// open create new
		if (openSheet) this.createNewDashboard(createNew);
	}

	createNewDashboard(createNew: boolean) {
		return this.sheet
			.open(SaveDashboardDialogComponent, {
				width: "480px",
				data: <DashboardConfig>{
					createNew: createNew,
					fromDateData: this.range.value.from,
					toDateData: this.range.value.to,
					rangePreset: this.range.value.name,
					contingentUntilDate: isSameDay(this.untilDate.value, new Date())
						? null
						: this.untilDate.value.toISOString(),
					personData: this.teamFilter.value,
					groupData: this.groupFilter.value,
					auditData: this.auditFilter.value,
					columns: [],
					comegoType: this.showFilters.value ? this.comegoTypeFilter.value : [],

					projectData: !this.showFilters.value ? this.projectFilter.value : [],
					clientData: !this.showFilters.value ? this.clientFilter.value : [],
					taskData: !this.showFilters.value ? this.taskFilter.value : [],
					tagData: !this.showFilters.value ? this.tagFilter.value : [],
					billable: !this.showFilters.value ? this.billableFilter.value : 0,
					tabType:
						this.currentPage.value === "contingent"
							? DashboardTabTypes.CONTINGENT
							: this.currentPage.value === "workinghours"
								? DashboardTabTypes.WORKING_HOURS
								: DashboardTabTypes.PROJECT_HOURS,
				},
			})
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				map((selectedDashboard) => {
					if (selectedDashboard) {
						this.dashboards.update((s) => [...s, selectedDashboard], true);
						this.lastDashboard.update(selectedDashboard.id);
						this.activeFavDashboard.next(selectedDashboard);
					}
				})
			)
			.subscribe();
	}
	async updateDashboardAfterConfirm(updateOrNewTemplate?: TemplateRef<any>, confirmUpdateDialog?: TemplateRef<any>) {
		const updateConfirmed = await toPromise(
			this.dialog
				.open(confirmUpdateDialog, {
					data: {
						entity: null,
					},
				})
				.afterClosed()
		);
		// update Dashboard
		if (updateConfirmed) {
			let dashboard = this.activeFavDashboard.value;
			dashboard = {
				...dashboard,
				fromDateData: this.range.value.from,
				toDateData: this.range.value.to,
				rangePreset: this.range.value.name,

				personData: this.teamFilter.value,
				groupData: this.groupFilter.value,
				auditData: this.auditFilter.value,

				projectData: !this.showFilters.value ? this.projectFilter.value : [],
				clientData: !this.showFilters.value ? this.clientFilter.value : [],
				taskData: !this.showFilters.value ? this.taskFilter.value : [],
				tagData: !this.showFilters.value ? this.tagFilter.value : [],
				comegoType: this.showFilters.value ? this.comegoTypeFilter.value : [],
				billable: !this.showFilters.value ? this.billableFilter.value : 0,
				contingentUntilDate: isSameDay(this.untilDate.value, new Date())
					? null
					: this.untilDate.value.toISOString(),
			};
			await toPromise(
				this.timesService.adapter.post<SavedDashboard>(
					environment.serverUrl + "/update/saved_dashboards/" + dashboard.id,
					dashboard
				)
			).then((x) => {
				//  reload saved dashboard data
				this.activeFavDashboard.next(x);
				this.dashboards.update((oldValues) => {
					let newValues = [...oldValues];
					const index = newValues.findIndex((d) => d.id == x.id);
					if (index == -1) return newValues;
					newValues[index] = {
						...newValues[index],
						...x,
					};
					return newValues;
				});
			});
			this.appService?.notifySuccess("success.updated");
			return false;
		} else {
			// ask user again if he wants to update or create a new dashboard
			this.savedDashboardMenu(true, updateOrNewTemplate, confirmUpdateDialog);
		}
	}
	applyDashboard(item: any, newSelection?: boolean) {
		if (newSelection) {
			this.lastDashboard.update(item.id);
		}
		this.activeFavDashboard.next(item);

		this.teamFilter.update(item.personData);
		this.projectFilter.update(item.projectData);
		if (item.tabType == 0 && item.columns) {
			this.appSettings.updateByKey("reports.export.columns", item.columns);
		} else if (item.tabType == 1 && item.columns) {
			this.appSettings.updateByKey("schedule.export.columns", item.columns);
		}

		this.groupFilter.update(item.groupData);
		this.clientFilter.update(item.clientData);
		this.taskFilter.update(item.taskData);
		this.billableFilter.update(item.billable);
		this.comegoTypeFilter.update(item.comegoType ?? []);
		this.untilDate.update(item.contingentUntilDate ?? new Date());

		this.auditFilter.value = item.auditData as AuditFilterData;

		// tag filter updaten
		const filtertags = this.tagsQuery
			.getAll()
			.filter((tag) => item.tagData.includes(tag.id))
			.map((tag) => {
				return { id: tag.id, name: tag.name, tagType: tag.tagType };
			});
		this.tagFilter.update(filtertags);

		// range filter updaten
		this.range.update(() => {
			this.range.value;
			const rType = getTimeRangeByKey(item.rangePreset);
			let newData = {
				...(rType || {}),
				rangeType:
					rType?.rangeType ?? (differenceInDays(item.fromDateData, item.toDateData) > 1 ? "week" : "day"),
			} as TRange;
			if (item.rangePreset === "time-range.preset.custom") {
				newData.rangeType = null;
				newData.from = new Date(item.fromDateData);
				newData.to = new Date(item.toDateData);
			}
			if (item.rangePreset == "time-range.preset.until-today") {
				newData.from = new Date(item.fromDateData);
			}
			if (rType) rType.afterParse?.(newData);
			return newData;
		});
		this.reportsService.syncRangeToUrl(this.range.value);
	}
}
const log = new Logger(ReportsComponent.name);

export interface IReportTimeStatistics {
	billed: string;
	earnings: number;
	nonBilled: string;
	summed: string;
}
type RangeFilterData = {
	projects: {
		name: string;
		id: string;
		completed: boolean;
		useAsDefault: boolean;
	}[];
	tasks: {
		name: string;
		id: string;
	}[];
	tags: Tag[];
};
